import {
  buttonVariants,
  cn,
  focusHaloFlush,
  HapticSurface,
  panelMotion,
  panelRow,
  press,
  Skeleton,
} from "@pax/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CircleUser, LogIn } from "lucide-react";
import { NotificationsButton } from "@/components/NotificationsButton";
import { CreditsPill } from "@/features/credits/components/CreditsPill";
import { usePhoneWidth } from "@/shell/use-phone-width";
import { useAccountDialog } from "../account-dialog";
import { sessionQuery, useCurrentProfile } from "../queries";
import { AccountAvatar } from "./AccountAvatar";

/** A 32px target around your 28px picture, the credits chip's height, ringed on hover. */
const pictureButton = `group relative grid size-8 shrink-0 place-items-center rounded-full ${press} ${focusHaloFlush}`;
const pictureRing = `rounded-full ring-2 ring-transparent group-hover:ring-foreground/15 ${panelMotion}`;

/**
 * Who the frame thinks you are, as the name and picture to draw.
 *
 * They come from your profile, which is read once the session says whose it
 * is. Until both answer the frame draws a skeleton rather than a guess.
 */
function useViewer() {
  const { data: session, isError } = useQuery(sessionQuery);
  const profile = useCurrentProfile(session?.user);

  if (session === null) return { kind: "none" } as const;
  if (!session) {
    return isError ? ({ kind: "unavailable" } as const) : ({ kind: "loading" } as const);
  }
  if (session.user.isAnonymous) {
    return { kind: "known", name: "Guest", image: null, profile: null } as const;
  }
  const own = { profileId: session.user.id, username: profile.data?.username ?? null };
  if (profile.data) {
    return {
      kind: "known",
      name: profile.data.displayName,
      image: profile.data.avatar,
      profile: own,
    } as const;
  }
  if (profile.isError)
    return { kind: "known", name: "Account", image: null, profile: own } as const;
  return { kind: "loading" } as const;
}

/**
 * Your picture at the end of the bar. It opens Settings › Profile, or on a phone the list of your
 * pages;
 * a guest has neither, so for a guest it offers to save progress to an account.
 */
export function BarAccountToggle() {
  const viewer = useViewer();
  const phone = usePhoneWidth();
  const { openAccountDialog } = useAccountDialog();

  if (viewer.kind === "loading") {
    return (
      <span aria-busy className="grid size-8 place-items-center">
        <span className="sr-only">Loading account</span>
        <Skeleton className="size-7 rounded-full" />
      </span>
    );
  }

  if (viewer.kind === "unavailable") {
    return (
      <span className="grid size-8 place-items-center rounded-full text-muted-foreground">
        <span className="sr-only">Checking account</span>
        <CircleUser aria-hidden className="size-4" />
      </span>
    );
  }

  if (viewer.kind === "none") {
    return (
      <Link
        to="/login"
        className={cn(buttonVariants({ variant: "ghost" }), "text-muted-foreground")}
      >
        <CircleUser aria-hidden className="size-4" />
        <span>Sign in</span>
        <HapticSurface />
      </Link>
    );
  }

  const picture = (
    <>
      <AccountAvatar name={viewer.name} image={viewer.image} size={28} className={pictureRing} />
      <HapticSurface />
    </>
  );
  // On a phone your picture opens the list of your pages, your profile first; elsewhere it opens
  // that first page, Profile, with the list beside it.
  return viewer.profile ? (
    <Link
      to="/settings/{-$section}"
      params={{ section: phone ? undefined : "profile" }}
      aria-label="You"
      className={pictureButton}
    >
      {picture}
    </Link>
  ) : (
    <button
      type="button"
      onClick={() => openAccountDialog()}
      aria-label="Save progress"
      className={pictureButton}
    >
      {picture}
    </button>
  );
}

/**
 * Your picture at the foot of the column, with notifications and your credits beside it. Pressing
 * it opens Settings › Profile, and the column slides over to your face: Profile and the other
 * settings sections.
 */
export function ColumnAccountToggle() {
  const viewer = useViewer();
  const { openAccountDialog } = useAccountDialog();

  if (viewer.kind === "loading") {
    return (
      <div aria-busy className={cn(panelRow, "pointer-events-none")}>
        <span className="sr-only">Loading account</span>
        <Skeleton className="size-6 shrink-0 rounded-full" />
        <Skeleton className="h-4 w-28" />
      </div>
    );
  }

  if (viewer.kind === "unavailable") {
    return (
      <div className={cn(panelRow, "pointer-events-none text-muted-foreground")}>
        <CircleUser aria-hidden className="size-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate">Checking account</span>
      </div>
    );
  }

  if (viewer.kind === "none") {
    return (
      <Link to="/login" className={cn(panelRow, "text-muted-foreground hover:text-foreground")}>
        <LogIn aria-hidden className="size-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate">Sign in</span>
        <HapticSurface />
      </Link>
    );
  }

  const picture = pictureButton;
  const face = (
    <>
      <AccountAvatar name={viewer.name} image={viewer.image} size={28} className={pictureRing} />
      <HapticSurface />
    </>
  );
  // One row for what is yours: your picture on the left, then notifications and your credits on
  // the right. Your picture opens your pages, Profile first.
  return (
    <div className="flex w-full items-center gap-0.5">
      {viewer.profile ? (
        <Link
          to="/settings/{-$section}"
          params={{ section: "profile" }}
          aria-label="You"
          title={viewer.name}
          className={picture}
        >
          {face}
        </Link>
      ) : (
        <button
          type="button"
          onClick={() => openAccountDialog()}
          aria-label="Save progress"
          title={viewer.name}
          className={picture}
        >
          {face}
        </button>
      )}
      <span className="flex-1" />
      <NotificationsButton />
      <CreditsPill />
    </div>
  );
}
